import * as React from "react";

import { useHttpClient } from "@/providers/HttpClient";

import { AutocompleteType } from "./types";

// Safety bound on how many pages of a paginated tag corpus (the
// datasets/events/files `/tags` endpoints, which page at a 5000-item
// backend default) this hook will follow. Without a cap, an org with an
// unbounded tag corpus would keep this effect fetching indefinitely; 10
// pages caps the fetch at 50,000 tags, comfortably above any real org's
// corpus, while still fixing the regression where only the first page
// (≤5000 items) was ever surfaced.
const MAX_PAGINATED_TAG_PAGES = 10;
const MAX_PAGINATED_TAGS = 50_000;

const autocompleteTypeToUrl = {
  [AutocompleteType.ActionEnvVarKeys]: "",
  [AutocompleteType.ActionMetadataKeys]: "actions/metadata/keys",
  [AutocompleteType.ActionTags]: "actions/tags",
  [AutocompleteType.CollectionTags]: "collections/tags",
  [AutocompleteType.DatasetMetadataKeys]: "datasets/metadata/keys",
  [AutocompleteType.DatasetTags]: "datasets/tags",
  [AutocompleteType.EventMetadataKeys]: "events/metadata/keys",
  [AutocompleteType.EventNames]: "events/names",
  [AutocompleteType.EventTags]: "events/tags",
  [AutocompleteType.FileMetadataKeys]: "files/metadata/keys",
  [AutocompleteType.FileTags]: "files/tags",
  [AutocompleteType.GenericMetadataKeys]: "",
  [AutocompleteType.GenericTags]: "",
  // No `/v1/sessions/{tags,metadata/keys}` corpus endpoints exist; suggestions
  // come from the `existingTags`/`existingMetadata` props the autocomplete
  // already merges in.
  [AutocompleteType.SessionMetadataKeys]: "",
  [AutocompleteType.SessionTags]: "",
  [AutocompleteType.SkillTags]: "skills/tags",
  [AutocompleteType.TopicMetadataKeys]: "topics/metadata/keys",
};

interface ElementsForAutocompleteState {
  elements: string[];
  error: Error | null;
}

/**
 * Normalize a corpus response to the flat suggestion list this hook promises.
 *
 * The routes it fans out over do NOT agree on a shape. The dataset, event, and
 * file ``/tags`` routes return a paginated ``{items, next_token}`` page — they
 * take ``search`` and ``limit`` so a large tag vocabulary isn't sent whole —
 * while every other route here (the remaining ``/tags``, the
 * ``/metadata/keys``, and ``/events/names``) still returns a bare list.
 *
 * The shape is checked at RUNTIME rather than asserted through the response
 * generic, because that generic is a cast and not a parse. When the dataset
 * tags route became paginated, the previous ``response.json<string[]>()``
 * carried on compiling and handed the component a page object, which then blew
 * up on ``.filter`` and took the datasets table and dataset detail page down
 * with it. A cast cannot catch that; this can.
 *
 * An unrecognized payload degrades to no suggestions rather than throwing: the
 * autocomplete is an optional aid to typing a tag by hand, so an empty
 * dropdown is a far better failure than an unmountable page.
 */
function toElements(payload: unknown): string[] {
  const isString = (item: unknown): item is string => typeof item === "string";

  if (Array.isArray(payload)) {
    return payload.filter(isString);
  }
  if (
    typeof payload === "object" &&
    payload !== null &&
    "items" in payload &&
    Array.isArray(payload.items)
  ) {
    return payload.items.filter(isString);
  }
  return [];
}

/**
 * Extract the next-page token from a paginated ``{items, next_token}``
 * response, or ``undefined`` for a bare array, an exhausted pagination, or an
 * unrecognized payload. Checked at runtime for the same reason as
 * ``toElements`` — a response generic is a cast, not a parse.
 */
function nextPageToken(payload: unknown): string | undefined {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "next_token" in payload &&
    typeof payload.next_token === "string"
  ) {
    return payload.next_token;
  }
  return undefined;
}

export const useElementsForAutocompleteType = (
  autocompleteType: AutocompleteType,
  existingElements: string[] = [],
): ElementsForAutocompleteState => {
  const [state, setState] = React.useState<ElementsForAutocompleteState>({
    elements: [],
    error: null,
  });

  const { httpClient } = useHttpClient();

  const arrayAsString = existingElements.sort().join();

  React.useEffect(() => {
    const urlPath = autocompleteTypeToUrl[autocompleteType];

    if (urlPath === "") {
      setState({
        elements: [],
        error: null,
      });
      return;
    }

    const abortController = new AbortController();

    const fetchElements = async () => {
      try {
        const elements: string[] = [];
        let pageToken: string | undefined;
        let pagesFetched = 0;

        do {
          const searchParams = new URLSearchParams();
          if (pageToken !== undefined) {
            searchParams.append("page_token", pageToken);
          }
          const url = httpClient.constructUrl(`v1/${urlPath}`, searchParams);
          const response = await httpClient.get(url, {
            signal: abortController.signal,
          });
          // The dataset/event/file tags endpoints return a paginated
          // `{ items, next_token }` object as of API version 2026-08-10; the
          // remaining corpus endpoints still return a bare array (and are
          // never paginated, so the loop below always exits after one pass).
          // `toElements` validates either shape at runtime.
          const payload = await response.json<unknown>();
          pagesFetched += 1;

          elements.push(...toElements(payload));
          pageToken = nextPageToken(payload);
        } while (
          pageToken !== undefined &&
          pagesFetched < MAX_PAGINATED_TAG_PAGES &&
          elements.length < MAX_PAGINATED_TAGS
        );

        setState({
          elements,
          error: null,
        });
      } catch (e) {
        if (abortController.signal.aborted) {
          return;
        }

        setState({
          elements: [],
          error: new Error("Failed to fetch elements for autocomplete type", {
            cause: e,
          }),
        });
      }
    };

    void fetchElements();

    return () => {
      abortController.abort();
    };
  }, [httpClient, autocompleteType, arrayAsString]);

  return state;
};
